import { CATEGORIES, CATEGORY_LABELS, type CategoryKey } from "../categories/generated.js";

/** Minimum, das ein Eintrag für die Gang-Sortierung mitbringen muss. */
export interface SortableItem {
  id: string;
  name: string;
  category: CategoryKey | null;
  sort_index?: number;
}

export interface CategorySection<T extends SortableItem> {
  key: CategoryKey | "ohne_kategorie";
  label: string;
  items: T[];
}

const FALLBACK_ORDER: CategoryKey[] = CATEGORIES.map((category) => category.key);

/**
 * Rang einer Kategorie in der Laufrichtung eines Ladens. Kategorien, die im
 * Ladenprofil fehlen, landen hinter den bekannten, aber vor "ohne Kategorie".
 */
export function categoryRank(category: CategoryKey | null, order: readonly CategoryKey[]): number {
  if (category === null) return Number.MAX_SAFE_INTEGER;
  const index = order.indexOf(category);
  if (index >= 0) return index;
  const fallback = FALLBACK_ORDER.indexOf(category);
  return order.length + (fallback >= 0 ? fallback : FALLBACK_ORDER.length);
}

/**
 * Sortiert Artikel in Laufrichtung des Ladens. Innerhalb einer Kategorie
 * entscheidet `sort_index`, danach der Name. Stabil, damit die Liste beim
 * Abhaken nicht springt.
 */
export function sortByStoreOrder<T extends SortableItem>(items: readonly T[], order: readonly CategoryKey[]): T[] {
  return [...items].sort((a, b) => {
    const rank = categoryRank(a.category, order) - categoryRank(b.category, order);
    if (rank !== 0) return rank;
    const byIndex = (a.sort_index ?? 0) - (b.sort_index ?? 0);
    if (byIndex !== 0) return byIndex;
    return a.name.localeCompare(b.name, "de-DE");
  });
}

/**
 * Gruppiert Artikel zu Gang-Abschnitten. Leere Abschnitte kommen nicht vor,
 * der Einkaufsmodus (Prompt 2) baut darauf auf.
 */
export function groupByCategory<T extends SortableItem>(
  items: readonly T[],
  order: readonly CategoryKey[],
): CategorySection<T>[] {
  const sorted = sortByStoreOrder(items, order);
  const sections: CategorySection<T>[] = [];

  for (const item of sorted) {
    const key = item.category ?? "ohne_kategorie";
    const last = sections.at(-1);
    if (last && last.key === key) {
      last.items.push(item);
      continue;
    }
    sections.push({
      key,
      label: item.category ? CATEGORY_LABELS[item.category] : "Ohne Kategorie",
      items: [item],
    });
  }

  return sections;
}

/**
 * Welcher Gang im Einkaufsmodus groß steht.
 *
 * Der gewählte, solange er Artikel hat. Ist er leer, der nächste in
 * Laufrichtung dahinter, nicht der erste der Liste: Wer bei der Molkerei
 * steht, geht weiter zu den Getränken und nicht zurück zum Obst am Eingang.
 * Erst wenn dahinter nichts mehr kommt, geht es wieder von vorn los. Ohne
 * Wahl gilt der erste Gang.
 *
 * Gebaut am 20.09.2026: Legte jemand zu Hause "Äpfel" an, während der andere
 * an der Molkerei stand, sprang dessen Bildschirm zum Obst, und nach dem
 * letzten Haken in der Molkerei ebenfalls.
 */
export function pickAisle<T extends SortableItem>(
  sections: readonly CategorySection<T>[],
  chosen: CategorySection<T>["key"] | null,
  order: readonly CategoryKey[],
): CategorySection<T> | undefined {
  if (chosen === null) return sections[0];

  const direct = sections.find((section) => section.key === chosen);
  if (direct) return direct;

  const rank = (key: CategorySection<T>["key"]) => (key === "ohne_kategorie" ? Number.MAX_SAFE_INTEGER : categoryRank(key, order));
  const grenze = rank(chosen);
  return sections.find((section) => rank(section.key) > grenze) ?? sections[0];
}
